// src/components/property/FinalVideoGeneration.tsx
/**
 * FinalVideoGeneration Component
 * Step 4 of property video wizard: Shows generation progress and final video
 */

import { memo, useState, useCallback, useRef } from 'react';
import {
  Video,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Download,
  RefreshCw,
  Play,
  Pause,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import type { IFinalVideoGenerationProps } from '@/@types/fullVideoGenerator';

// ============================================================================
// Helper Functions
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
}

// ============================================================================
// Video Player Sub-Component
// ============================================================================

interface IVideoPlayerProps {
  videoUrl: string;
}

function VideoPlayer({ videoUrl }: IVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  return (
    <div className="relative aspect-[9/16] w-full max-w-[320px] overflow-hidden rounded-lg bg-gray-900 mx-auto">
      <video
        ref={videoRef}
        src={videoUrl}
        className="h-full w-full object-cover"
        onEnded={handleEnded}
        playsInline
      />
      <button
        type="button"
        onClick={togglePlay}
        className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 transition-opacity hover:opacity-100"
        aria-label={isPlaying ? 'Pause video' : 'Play video'}
      >
        <div className="rounded-full bg-white/90 p-4">
          {isPlaying ? (
            <Pause className="h-6 w-6 text-gray-900" aria-hidden="true" />
          ) : (
            <Play className="h-6 w-6 text-gray-900" aria-hidden="true" />
          )}
        </div>
      </button>
    </div>
  );
}

// ============================================================================
// Progress Display Sub-Component
// ============================================================================

interface IProgressDisplayProps {
  status: string;
  progress: number;
  videosProcessed: number;
  totalVideos: number;
}

function ProgressDisplay({
  status,
  progress,
  videosProcessed,
  totalVideos,
}: IProgressDisplayProps) {
  const getStatusMessage = (): string => {
    switch (status) {
      case 'downloading':
        return `Downloading videos (${videosProcessed}/${totalVideos})...`;
      case 'normalizing':
        return `Normalizing videos (${videosProcessed}/${totalVideos})...`;
      case 'concatenating':
        return 'Creating final video...';
      case 'uploading':
        return 'Saving video...';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
          <span className="text-sm font-medium">{getStatusMessage()}</span>
        </div>
        <span className="text-sm font-medium text-muted-foreground">{progress}%</span>
      </div>
      <Progress value={progress} className="h-3" />
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

function FinalVideoGenerationComponent({
  generatorState,
  finalVideoUrl,
  videoMetadata,
  onRegenerate,
  onReset,
}: IFinalVideoGenerationProps) {
  const { status, progress, error, videosProcessed, totalVideos } = generatorState;

  const isProcessing =
    status === 'downloading' ||
    status === 'normalizing' ||
    status === 'concatenating' ||
    status === 'uploading';

  const isCompleted = status === 'completed' || (status === 'idle' && finalVideoUrl);
  const isError = status === 'error';

  const handleDownload = useCallback(async () => {
    if (!finalVideoUrl) return;
    try {
      const response = await fetch(finalVideoUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `property-video-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download video:', error);
    }
  }, [finalVideoUrl]);

  const handleRegenerate = useCallback(async () => {
    onReset();
    await onRegenerate();
  }, [onRegenerate, onReset]);

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isCompleted ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" aria-hidden="true" />
          ) : isError ? (
            <AlertCircle className="h-5 w-5 text-destructive" aria-hidden="true" />
          ) : (
            <Video className="h-5 w-5" aria-hidden="true" />
          )}
          {isCompleted ? 'Video Ready' : isError ? 'Generation Failed' : 'Creating Your Video'}
        </CardTitle>
        <CardDescription>
          {isCompleted
            ? 'Your property video has been generated successfully.'
            : isError
              ? 'There was an error generating your video.'
              : 'Please wait while we create your property video.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Error State */}
        {isError && error && (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="rounded-lg bg-destructive/10 p-4 text-center">
              <p className="text-sm text-destructive">{error}</p>
            </div>
            <Button onClick={handleRegenerate} variant="outline" className="gap-2">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Try Again
            </Button>
          </div>
        )}

        {/* Processing State */}
        {isProcessing && (
          <div className="py-8">
            <ProgressDisplay
              status={status}
              progress={progress}
              videosProcessed={videosProcessed}
              totalVideos={totalVideos}
            />
          </div>
        )}

        {/* Completed State */}
        {isCompleted && finalVideoUrl && (
          <div className="flex flex-col items-center gap-6">
            <VideoPlayer videoUrl={finalVideoUrl} />

            {/* Video Metadata */}
            {videoMetadata && (
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>{formatDuration(videoMetadata.durationSeconds)}</span>
                <span>•</span>
                <span>{formatFileSize(videoMetadata.fileSizeBytes)}</span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button onClick={handleDownload} className="gap-2">
                <Download className="h-4 w-4" aria-hidden="true" />
                Download
              </Button>
              <Button onClick={handleRegenerate} variant="outline" className="gap-2">
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Regenerate
              </Button>
            </div>
          </div>
        )}

        {/* Idle State (waiting to start) */}
        {status === 'idle' && !finalVideoUrl && (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <Video className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              Click "Generate Full Video" on the previous step to start.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const FinalVideoGeneration = memo(FinalVideoGenerationComponent);
